import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { GraduationCap, ChevronRight, ChevronLeft, Check } from 'lucide-react';

const TutorialMode = ({ onNavigate }) => {
  const { t } = useLanguage();
  const TUTORIAL_STEPS = t('tutorial.steps');
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const step = TUTORIAL_STEPS[currentStep];

  const markCompleted = () => {
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const progress = (completedSteps.size / TUTORIAL_STEPS.length) * 100;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Título de página */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">{t('tutorial.title')}</h1>
        <p className="text-lg text-white/90">{t('tutorial.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Barra de progreso */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-5 h-5" />
              <span className="font-bold">{t('tutorial.progress')}</span>
            </div>
            <span className="text-sm text-white/80">
              {t('tutorial.completed', { done: completedSteps.size, total: TUTORIAL_STEPS.length })}
            </span>
          </div>
          <div className="bg-white/30 rounded-full h-2">
            <div
              className="bg-white rounded-full h-2 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Sidebar con listado de pasos */}
          <div className="lg:w-72 border-b lg:border-b-0 lg:border-r border-gray-100 p-4 bg-gray-50 shrink-0">
            <div className="space-y-1">
              {TUTORIAL_STEPS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentStep(i)}
                  className={`w-full text-left flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                    i === currentStep
                      ? 'bg-orange-500 text-white font-semibold'
                      : completedSteps.has(i)
                      ? 'bg-green-50 text-green-700 hover:bg-green-100'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      completedSteps.has(i)
                        ? 'bg-green-500 text-white'
                        : i === currentStep
                        ? 'bg-white text-orange-500'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {completedSteps.has(i) ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Contenido del paso actual */}
          <div className="flex-1 p-6 flex flex-col justify-between">
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-5xl">{step.icon}</span>
                <h2 className="text-2xl font-bold text-gray-800">{step.title}</h2>
              </div>
              <p className="text-gray-600 text-base leading-relaxed mb-4">{step.content}</p>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{t('tutorial.tip')}</strong> {step.tip}
                </p>
              </div>
            </div>

            {/* Navegación */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-30 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>{t('tutorial.prev')}</span>
              </button>

              <button
                onClick={() => {
                  markCompleted();
                  if (onNavigate && step.tab) onNavigate(step.tab);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                {completedSteps.has(currentStep) ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>{t('tutorial.markDone')}</span>
                  </>
                ) : (
                  <span>{t('tutorial.practice')}</span>
                )}
              </button>

              <button
                onClick={() => setCurrentStep(Math.min(TUTORIAL_STEPS.length - 1, currentStep + 1))}
                disabled={currentStep === TUTORIAL_STEPS.length - 1}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-30 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span>{t('tutorial.next')}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialMode;


